import { SmsGroupTemplateType } from "@/models/club";
import { useGetSmsPlaceHolder } from "@/services/smsPlaceHolders/hooks/useGetSmsPlaceHolder";
import smsTemplateService from "@/services/template";
import { Button, Collapse, Popover, Skeleton, TextInput } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCheck, IconChevronDown, IconClock, IconLock, IconMessage, IconMoodSmile, IconPlus, IconX } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import EmojiPicker from "emoji-picker-react";
import { ReactNode, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import MessageEditor, { MessageEditorHandle } from "./MessageEditor";
import {
  OPT_OUT, TEMPLATE_TEXT_MAX, TEMPLATE_TITLE_MAX, applicableTypes, faNumber, groupPlaceholders, nextSmsLabel,
  previewText, profileLinkFor, smsSegments, stripOptOut, withOptOut,
} from "./sms";
import classes from "./sms.module.scss";

type Props = {
  initialTitle?: string;
  initialText?: string;
  banner?: ReactNode;
  showStarters?: boolean;
  collapseVariables?: boolean;
  submitLabel: string;
  loading?: boolean;
  onSubmit: (values: { title: string; text: string }) => void;
};

export default function TemplateForm({ initialTitle = "", initialText = "", banner, showStarters, collapseVariables, submitLabel, loading, onSubmit }: Props) {
  const placeholders = useGetSmsPlaceHolder();
  const tags = useMemo(() => placeholders.data ?? [], [placeholders.data]);
  const editor = useRef<MessageEditorHandle>(null);
  const [title, setTitle] = useState(initialTitle);
  const [text, setText] = useState(initialText);
  const [touched, setTouched] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [variablesOpen, { toggle: toggleVariables }] = useDisclosure(!collapseVariables);

  const starters = useQuery({
    queryKey: ["allSmsTemplate", SmsGroupTemplateType.Value0],
    queryFn: () => smsTemplateService.getAllSmsTemplate(SmsGroupTemplateType.Value0),
    enabled: !!showStarters,
  });
  const starterList = (starters.data ?? []).flatMap((group) => group.templates ?? []).filter((t) => t.title && t.text).slice(0, 6);

  const body = text.trim();
  const preview = body ? withOptOut(previewText(body, tags)) : "";
  const length = Array.from(preview).length;
  const { segments, remaining } = smsSegments(length);
  const uses = applicableTypes(body, tags);
  const groups = groupPlaceholders(tags);

  const titleError = !title.trim() ? "نام قالب را بنویسید" : undefined;
  const textError = !body ? "متن پیامک را بنویسید" : Array.from(withOptOut(body)).length > TEMPLATE_TEXT_MAX ? `متن حداکثر ${faNumber(TEMPLATE_TEXT_MAX)} نویسه می‌تواند باشد` : undefined;

  const submit = () => {
    setTouched(true);
    if (titleError || textError) return;
    onSubmit({ title: title.trim(), text: withOptOut(body) });
  };

  return (
    <>
      <div className={classes.body}>
        {banner}

        <section className={classes.card} style={{ gap: 8 }}>
          <TextInput
            label={<span className={classes.label}>نام قالب</span>}
            placeholder="مثلاً تخفیف تولد"
            size="md"
            radius={12}
            maxLength={TEMPLATE_TITLE_MAX}
            value={title}
            onChange={(event) => setTitle(event.currentTarget.value)}
            error={touched ? titleError : undefined}
          />
          <div className={classes.helperRow}>
            <span>فقط برای خودتان؛ مشتری آن را نمی‌بیند.</span>
            <span>{faNumber(Array.from(title).length)} / {faNumber(TEMPLATE_TITLE_MAX)}</span>
          </div>
        </section>

        <section className={classes.card}>
          <h2 className={classes.cardTitle}>متن پیامک</h2>
          {showStarters && (starters.isLoading || starterList.length > 0) && (
            <div className={classes.varGroup}>
              <span className={classes.subLabel}>شروع سریع از متن‌های پیشنهادی</span>
              <div className={classes.chipRow}>
                {starters.isLoading
                  ? [0, 1, 2].map((i) => <Skeleton key={i} height={36} width={96} radius="xl" />)
                  : starterList.map((starter) => (
                      <button
                        type="button"
                        key={starter.id}
                        className={classes.choiceChip}
                        onClick={() => {
                          editor.current?.setText(stripOptOut(starter.text));
                          if (!title.trim()) setTitle((starter.title ?? "").slice(0, TEMPLATE_TITLE_MAX));
                        }}
                      >
                        {starter.title}
                      </button>
                    ))}
              </div>
            </div>
          )}

          <div className={classes.editorBox} data-error={touched && !!textError}>
            <MessageEditor ref={editor} initialText={initialText} tags={tags} onChange={setText} invalid={touched && !!textError} describedBy="sms-counter" />
            <div className={classes.lockedFooter}>
              <IconLock size={15} stroke={1.8} aria-hidden />
              <strong>{OPT_OUT}</strong>
              <span>خودکار به انتهای پیامک اضافه می‌شود</span>
            </div>
          </div>
          {touched && textError && <p className={classes.errorText}>{textError}</p>}

          <div className={classes.editorTools}>
            <Popover opened={emojiOpen} onChange={setEmojiOpen} position="bottom-start" shadow="md" withinPortal>
              <Popover.Target>
                <button type="button" className={classes.toolButton} aria-label="افزودن ایموجی" onClick={() => setEmojiOpen((open) => !open)}>
                  <IconMoodSmile size={21} stroke={1.8} />
                </button>
              </Popover.Target>
              <Popover.Dropdown p={0}>
                <EmojiPicker
                  height={320}
                  width={300}
                  searchDisabled
                  previewConfig={{ showPreview: false }}
                  onEmojiClick={(emoji) => {
                    editor.current?.insertText(emoji.emoji);
                    setEmojiOpen(false);
                  }}
                />
              </Popover.Dropdown>
            </Popover>
            <button type="button" className={classes.textButton} onClick={() => editor.current?.setText("")} disabled={!text}>
              پاک کردن
            </button>
            <div className={classes.counter} id="sms-counter" aria-live="polite">
              <span className={classes.counterMain}>
                {faNumber(length)} نویسه، <span>{faNumber(segments)} پیامک</span>
              </span>
              <span className={classes.counterSub}>
                {length ? `${faNumber(remaining)} نویسه تا پیامک ${nextSmsLabel(segments)}` : "هنوز متنی ننوشته‌اید"}
              </span>
            </div>
          </div>
          <p className={classes.fineprint}>هر پیامک فارسی تا ۷۰ نویسه است و در پیامک چندبخشی هر بخش ۶۷ نویسه. تعداد با طول نام هر مشتری کمی فرق می‌کند.</p>
        </section>

        <section className={classes.card}>
          {collapseVariables ? (
            <button type="button" className={classes.collapseToggle} aria-expanded={variablesOpen} onClick={toggleVariables}>
              <IconPlus size={18} stroke={2.2} aria-hidden />
              <span className={classes.noticeText} style={{ gap: 0, textAlign: "start" }}>
                افزودن اطلاعات خودکار
                <small>نام، آدرس، کد تخفیف و…</small>
              </span>
              <IconChevronDown size={18} stroke={1.8} aria-hidden />
            </button>
          ) : (
            <div className={classes.noticeText} style={{ gap: 3 }}>
              <h2 className={classes.cardTitle}>اطلاعات خودکار</h2>
              <span className={classes.mutedLabel} style={{ lineHeight: 1.8 }}>روی هر مورد بزنید تا در متن قرار بگیرد. هنگام ارسال، اطلاعات واقعی هر مشتری جایگزین می‌شود.</span>
            </div>
          )}
          <Collapse in={variablesOpen}>
            <div className={classes.noticeText} style={{ gap: 12 }}>
              {placeholders.isLoading && <Skeleton height={80} radius="md" />}
              {groups.map((group) => (
                <div key={group.name} className={classes.varGroup}>
                  <div className={classes.varGroupHead}>
                    {group.name}
                    {group.note && <span className={classes.varNote}>{group.note}</span>}
                  </div>
                  <div className={classes.chipRow}>
                    {group.tags.map((tag) =>
                      tag.hasValue ? (
                        <button type="button" key={tag.key} className={classes.varChip} onClick={() => editor.current?.insertTag(tag)}>
                          <IconPlus size={14} stroke={2.4} aria-hidden />
                          {tag.value}
                        </button>
                      ) : (
                        <Link key={tag.key} to={profileLinkFor(tag)} className={classes.varChip} data-missing="true">
                          {tag.value}
                          <span>(تکمیل در پروفایل)</span>
                        </Link>
                      ),
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Collapse>
        </section>

        <section className={classes.card} style={{ gap: 14 }}>
          <div className={classes.previewHead}>
            <span className={classes.previewIcon}>
              <IconMessage size={20} stroke={1.8} aria-hidden />
            </span>
            <div className={classes.noticeText} style={{ gap: 1 }}>
              <h2 className={classes.cardTitle}>پیش‌نمایش</h2>
              <span className={classes.mutedLabel} style={{ fontSize: 11.5 }}>با اطلاعات نمونه</span>
            </div>
          </div>
          <div className={classes.previewStage}>
            <p className={classes.bubble}>{preview || "پیش‌نمایش پیامک اینجا نمایش داده می‌شود."}</p>
          </div>
          <div className={classes.varGroup} style={{ gap: 8 }}>
            <span className={classes.varGroupHead}>قابل استفاده در</span>
            {uses.map((use) => (
              <div key={use.type} className={classes.useRow}>
                <span className={classes.useIcon} data-on={use.allowed}>
                  {use.allowed ? <IconCheck size={14} stroke={2.6} /> : <IconX size={13} stroke={2.6} />}
                </span>
                <span style={use.allowed ? { fontWeight: 700 } : { color: "#5f6477" }}>{use.label}</span>
                {!use.allowed && <span className={classes.useReason}>به‌خاطر «{use.blocker}»</span>}
              </div>
            ))}
          </div>
        </section>

        <div className={classes.notice}>
          <IconClock size={19} stroke={1.8} aria-hidden />
          <span>بعد از ثبت، پدیکس قالب را بررسی می‌کند. وضعیت را در «قالب‌های من» می‌بینید و پس از تأیید در همه‌ی ارسال‌ها قابل انتخاب است.</span>
        </div>
      </div>

      <div className={classes.stickyBar}>
        <Button fullWidth h={52} radius={14} color="myColor.7" fz={15} fw={800} loading={loading} onClick={submit}>
          {submitLabel}
        </Button>
      </div>
    </>
  );
}
